import { useEffect, useState } from "react";
import { Calendar, Check, Loader2, Link2Off, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function GoogleCalendarCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try { const r = await api.get("/calendar/status"); setStatus(r.data); }
    catch { setStatus({ connected: false, configured: false }); }
  };

  useEffect(() => {
    loadStatus();
    const params = new URLSearchParams(window.location.search);
    const c = params.get("calendar");
    if (c === "connected") { toast.success("Google Calendar conectado. Tus citas se sincronizarán automáticamente."); }
    if (c === "error") { toast.error("No se pudo conectar Google Calendar. Intenta de nuevo."); }
    if (c) { window.history.replaceState({}, "", window.location.pathname); }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/calendar/connect", { origin: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/calendar/disconnect"); await loadStatus(); toast.success("Google Calendar desconectado"); }
    catch { toast.error("No se pudo desconectar"); }
    finally { setBusy(false); }
  };

  if (!status || status.configured === false) return null;

  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-4 flex items-center gap-3 shadow-[0_4px_16px_rgb(61,64,91,0.03)]" data-testid="gcal-card">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${status.connected ? "bg-emerald-100 text-emerald-600" : "bg-brand-sage/15 text-brand-sageDark"}`}>
        <Calendar size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-outfit font-semibold text-brand-ink text-sm flex items-center gap-1.5">
          Google Calendar {status.connected && <Check size={15} className="text-emerald-600" />}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {status.connected ? `Conectado (${status.email || "cuenta de Google"}). Tus citas se sincronizan en ambos sentidos.` : "Conecta tu calendario para agendar consultas, cirugías y tiempo libre en un solo lugar."}
        </p>
      </div>
      {status.connected ? (
        <div className="flex items-center gap-2 shrink-0">
          <a data-testid="gcal-open" href="https://calendar.google.com" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs font-bold text-white bg-brand-ink hover:opacity-90 rounded-xl px-3 py-2 transition-all">
            <ExternalLink size={14} /> Abrir Google Calendar
          </a>
          <button data-testid="gcal-disconnect" onClick={disconnect} disabled={busy}
            className="flex items-center gap-1.5 text-xs font-bold text-brand-ink/70 hover:text-red-600 border border-stone-200 rounded-xl px-3 py-2 transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2Off size={14} />} Desconectar
          </button>
        </div>
      ) : (
        <button data-testid="gcal-connect" onClick={connect} disabled={busy}
          className="flex items-center gap-1.5 text-xs font-bold text-white bg-brand-ink hover:opacity-90 rounded-xl px-4 py-2.5 transition-all disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Vincular Google Calendar
        </button>
      )}
    </div>
  );
}
